import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  useSettings,
  ThemeOption,
  SoundOption,
  PaletteOption,
  ClockOrder,
} from "@/hooks/useSettings";
import { useTheme } from "next-themes";
import { BackgroundOption } from "@/components/backgrounds";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { SPOTIFY_CLIENT_ID } from "@/lib/spotify/constants";
import SpotifyLoginButton from "./SpotifyLoginButton";
import SpotifyPlaylistPicker from "./SpotifyPlaylistPicker";
import { useSpotifyAuth } from "@/hooks/useSpotifyAuth";

type SettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function getTimezoneGroups(): Record<string, string[]> {
  const timezones = Intl.supportedValuesOf("timeZone");
  const groups: Record<string, string[]> = {};
  for (const tz of timezones) {
    const region = tz.split("/")[0];
    if (!groups[region]) groups[region] = [];
    groups[region].push(tz);
  }
  return groups;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { settings, setTheme, setSound, setBackground, setPalette, setClock } =
    useSettings();
  const { setTheme: setThemeNext } = useTheme();
  const { isAuthenticated } = useSpotifyAuth();
  const [tzOpen, setTzOpen] = useState(false);

  const timezoneGroups = useMemo(() => getTimezoneGroups(), []);

  const hasSpotifyClientId = !!SPOTIFY_CLIENT_ID;

  // Sync next-themes when settings.theme changes via modal
  useEffect(() => {
    setThemeNext(settings.theme);
  }, [settings.theme, setThemeNext]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>
            Customize theme, background, and sounds. Tip: Press Ctrl/Cmd + B to
            toggle this panel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Clock */}
          <section className="space-y-3">
            <Label className="text-base">Clock</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
              <div className="col-span-1 flex items-center gap-2">
                <Switch
                  checked={settings.clock.showSeconds}
                  onCheckedChange={(c) => setClock({ showSeconds: c })}
                  id="show-seconds"
                />
                <Label htmlFor="show-seconds">Show seconds</Label>
              </div>
              <div className="col-span-1 flex items-center gap-2">
                <Switch
                  checked={settings.clock.blinkSeparators}
                  onCheckedChange={(c) => setClock({ blinkSeparators: c })}
                  id="blink-separators"
                />
                <Label htmlFor="blink-separators">Blink separators</Label>
              </div>
              <div className="col-span-1 flex items-center gap-2">
                <Switch
                  checked={settings.clock.use24Hour}
                  onCheckedChange={(c) => setClock({ use24Hour: c })}
                  id="use-24-hour"
                />
                <Label htmlFor="use-24-hour">24-hour format</Label>
              </div>
              <div className="col-span-1">
                <Label className="text-sm">Layout</Label>
                <Select
                  value={settings.clock.order}
                  onValueChange={(v) => setClock({ order: v as ClockOrder })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Layout" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="time-date">
                      Clock first, then date
                    </SelectItem>
                    <SelectItem value="date-time">
                      Date first, then clock
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <Label className="text-sm">Timezone</Label>
                <Popover open={tzOpen} onOpenChange={setTzOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={tzOpen}
                      className="w-full justify-between mt-1 font-normal"
                    >
                      {settings.clock.timezone}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search timezone..." />
                      <CommandList>
                        <CommandEmpty>No timezone found.</CommandEmpty>
                        {Object.entries(timezoneGroups).map(
                          ([region, zones]) => (
                            <CommandGroup key={region} heading={region}>
                              {zones.map((tz) => (
                                <CommandItem
                                  key={tz}
                                  value={tz}
                                  onSelect={() => {
                                    setClock({ timezone: tz });
                                    setTzOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      settings.clock.timezone === tz
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  {tz.replace(/_/g, " ")}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          ),
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </section>

          {/* Color Palette */}
          <section className="space-y-2">
            <Label className="text-base">Color palette</Label>
            <Select
              value={settings.palette}
              onValueChange={(v) => setPalette(v as PaletteOption)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select palette" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rose">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: "hsl(341 91% 60%)" }}
                    />
                    <span>Rose</span>
                  </div>
                </SelectItem>
                <SelectItem value="violet">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: "hsl(262 83% 58%)" }}
                    />
                    <span>Violet</span>
                  </div>
                </SelectItem>
                <SelectItem value="blue">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: "hsl(217.2 91.2% 59.8%)" }}
                    />
                    <span>Blue</span>
                  </div>
                </SelectItem>
                <SelectItem value="emerald">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: "hsl(142.1 70.6% 45.3%)" }}
                    />
                    <span>Emerald</span>
                  </div>
                </SelectItem>
                <SelectItem value="amber">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: "hsl(38 92% 50%)" }}
                    />
                    <span>Amber</span>
                  </div>
                </SelectItem>
                <SelectItem value="cyan">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: "hsl(188 94% 42%)" }}
                    />
                    <span>Cyan</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </section>

          {/* Theme */}
          <section className="space-y-2">
            <Label className="text-base">Theme</Label>
            <Select
              value={settings.theme}
              onValueChange={(v) => setTheme(v as ThemeOption)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select theme" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </section>

          {/* Sounds */}
          <section className="space-y-3">
            <Label className="text-base">Background sounds</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
              <div className="col-span-1">
                <Label className="text-sm">Type</Label>
                <Select
                  value={settings.sound.type}
                  onValueChange={(v) => setSound({ type: v as SoundOption })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select sound" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="rain">Rain</SelectItem>
                    {hasSpotifyClientId && (
                      <SelectItem value="spotify">
                        <div className="flex items-center gap-2">
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4 fill-[#1DB954]"
                          >
                            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                          </svg>
                          <span>Spotify</span>
                        </div>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1">
                <Label className="text-sm">
                  Volume: {settings.sound.volume}
                </Label>
                <Slider
                  value={[settings.sound.volume]}
                  onValueChange={(v) => setSound({ volume: v[0] })}
                  min={0}
                  max={100}
                  step={1}
                  className="mt-3"
                />
              </div>
              {settings.sound.type !== "spotify" && (
                <div className="col-span-1 flex items-center gap-2">
                  <Switch
                    checked={settings.sound.autoplay}
                    onCheckedChange={(c) => setSound({ autoplay: c })}
                    id="autoplay"
                  />
                  <Label htmlFor="autoplay">Autoplay on load</Label>
                </div>
              )}
            </div>

            {/* Spotify settings */}
            {settings.sound.type === "spotify" && hasSpotifyClientId && (
              <div className="space-y-3 pt-2 border-t border-border">
                <SpotifyLoginButton />
                {isAuthenticated && (
                  <>
                    <div>
                      <Label className="text-sm">Select playlist</Label>
                      <div className="mt-1">
                        <SpotifyPlaylistPicker />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Requires Spotify Premium for in-browser playback
                    </p>
                  </>
                )}
              </div>
            )}
          </section>

          {/* Background */}
          <section className="space-y-3">
            <Label className="text-base">Background</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
              <div className="col-span-1">
                <Label className="text-sm">Direction</Label>
                <Select
                  value={settings.background.type}
                  onValueChange={(v) =>
                    setBackground({
                      type: v as (typeof BackgroundOption)[keyof typeof BackgroundOption],
                    })
                  }
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BackgroundOption).map(([key, value]) => (
                      <SelectItem key={value} value={value}>
                        {key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
